import React, { useState } from 'react';
import {
  Paper,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Box,
  Chip,
  Collapse,
  Tooltip,
} from '@mui/material';
import {
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckedIcon,
  RadioButtonUnchecked as UncheckedIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { OptimizedTextField } from '../common/OptimizedTextField';

interface TicketFiltersProps {
  filters: any;
  onFilterChange: (filters: any) => void;
  onClearFilters: () => void;
  onLoadTickets: () => void;
  onNewTicket?: () => void;
  companies: any[];
  users: any[];
  modules: any[];
  statuses: any[];
  priorities: any[];
  authUser?: any; 
}

export const TicketFilters: React.FC<TicketFiltersProps> = ({
  filters,
  onFilterChange,
  onClearFilters,
  onLoadTickets,
  onNewTicket,
  companies,
  users,
  modules,
  statuses,
  priorities,
  authUser,
}) => {
  const [detailedFilterOpen, setDetailedFilterOpen] = useState(false);

  const handleFilterChange = (filterName: string, value: any) => {
    onFilterChange({
      ...filters,
      [filterName]: value
    });
  };

  const handleDateRangeChange = (type: 'created' | 'updated', range: 'start' | 'end', value: string) => {
    onFilterChange({
      ...filters,
      [type === 'created' ? 'dateRangeCreated' : 'dateRangeUpdated']: {
        ...filters[type === 'created' ? 'dateRangeCreated' : 'dateRangeUpdated'],
        [range]: value
      }
    });
  };

  const supportUsers = users.filter(u => Number(u.role_id) === 2);

  const today = new Date();
  const defaultStart = toDateInput(new Date(today.getFullYear(), 0, 1));
  const defaultEnd = toDateInput(today);

  function toDateInput(d: Date) {
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }

  const commonButtonSx = {
    fontSize: '0.7rem',
    fontWeight: 'bold',
    height: '32px',
    minWidth: 'fit-content',
    px: { xs: 1, md: 1.5 },
    py: 0.5,
    whiteSpace: 'nowrap',
    textTransform: 'none',
  };

  // Chip helper functions
  const getFilterStatusChip = (statusId: number) => {
    const status = statuses.find(s => s.id === statusId);
    if (!status) return '❓ Bilinmeyen';

    const statusConfig: any = {
      1: { icon: '🆕', label: 'Yeni' },
      2: { icon: '👤', label: 'Atandı' },
      3: { icon: '🚀', label: 'Başlandı' },
      4: { icon: '🧪', label: 'Test' },
      5: { icon: '↩️', label: 'Testten Döndü' },
      6: { icon: '✅', label: 'Tamamlandı' },
      7: { icon: '⏳', label: 'Bekliyor' },
      8: { icon: '🔒', label: 'Kapandı' },
    };

    const config = statusConfig[statusId] || { icon: '❓', label: status.name };
    return `${config.icon} ${config.label}`;
  };

  const getFilterPriorityChip = (priorityId: number) => {
    const priority = priorities.find(p => p.id === priorityId);
    if (!priority) return '❓ Bilinmeyen';

    const priorityConfig: any = {
      1: { icon: '🟢', label: 'Düşük' },
      2: { icon: '🔵', label: 'Orta' },
      3: { icon: '🟠', label: 'Yüksek' },
      4: { icon: '🔴', label: 'Acil' },
    };

    const config = priorityConfig[priorityId] || { icon: '❓', label: priority.name };
    return `${config.icon} ${config.label}`;
  };

  return (
    <Paper sx={{ p: 1, pb: 0.5, mb: 1 }}>
      <Grid container spacing={1} alignItems="center">
        {/* Oluşturma Tarihi */}
        <Grid item xs={12} md={2.2} sx={{ mt: 1 }}>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <TextField
              fullWidth
              type="date"
              label="Oluşturma Başlangıç"
              value={filters.dateRangeCreated.start}
              onChange={(e) => handleDateRangeChange('created', 'start', e.target.value)}
              InputLabelProps={{ 
                shrink: true,
                sx: { fontSize: '0.75rem', lineHeight: '1.2' }
              }}
              sx={{ 
                '& .MuiInputBase-input': { 
                  fontSize: '0.75rem',
                  padding: '6px 8px',
                  height: '24px !important'
                }
              }}
            />
            <TextField
              fullWidth
              type="date"
              label="Oluşturma Bitiş"
              value={filters.dateRangeCreated.end}
              onChange={(e) => handleDateRangeChange('created', 'end', e.target.value)}
              InputLabelProps={{ 
                shrink: true,
                sx: { fontSize: '0.75rem', lineHeight: '1.2' }
              }}
              sx={{ 
                '& .MuiInputBase-input': { 
                  fontSize: '0.75rem',
                  padding: '6px 8px',
                  height: '24px !important'
                }
              }}
            />
          </Box>
        </Grid>

        {/* Durum Filtresi */}
        <Grid item xs={12} md={1.5} sx={{ mt: 1 }}>
          <FormControl fullWidth size="small">
            <InputLabel sx={{ fontSize: '0.75rem', lineHeight: '1.2' }}>Durum</InputLabel>
            <Select 
              value={filters.status} 
              label="Durum" 
              onChange={(e) => handleFilterChange('status', typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
              multiple
              renderValue={(selected) => {
                if (selected.length === 0) return 'Durum';
                if (selected.length === 1) {
                  const status = statuses.find(s => s.id.toString() === selected[0]);
                  return status ? status.name : '1 adet';
                }
                return `${selected.length} adet seçili`;
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    maxHeight: 300,
                    '& .MuiMenuItem-root': {
                      paddingLeft: '8px',
                      paddingRight: '8px',
                      justifyContent: 'flex-start'
                    }
                  }
                }
              }}
              sx={{
                '& .MuiSelect-select': { 
                  fontSize: '0.75rem',
                  padding: '6px 15px',
                  minHeight: '24px !important',
                  lineHeight: '1.2',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-start'
                }
              }}
            >
              {statuses.map(status => {
                const isSelected = filters.status.includes(status.id.toString());
                return (
                  <MenuItem 
                    key={status.id} 
                    value={status.id.toString()} 
                    sx={{ 
                      fontSize: '0.75rem', 
                      py: 0.5,
                      display: 'flex',
                      justifyContent: 'flex-start',
                      paddingLeft: '8px',
                      fontWeight: isSelected ? 'bold' : 'normal'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                      {isSelected ? (
                        <CheckedIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                      ) : (
                        <UncheckedIcon sx={{ fontSize: 16, color: 'grey.400' }} />
                      )}
                      {getFilterStatusChip(status.id)}
                    </Box>
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </Grid>

        {/* Atanan Kişi - Sadece Admin */}
        {authUser?.role === 'admin' && (
          <Grid item xs={12} md={1.5} sx={{ mt: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel sx={{ fontSize: '0.75rem', lineHeight: '1.2' }}>Atanan</InputLabel>
              <Select 
                value={filters.assigned_to} 
                label="Atanan" 
                onChange={(e) => handleFilterChange('assigned_to', typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                multiple
                renderValue={(selected) => {
                  if (selected.length === 0) return 'Atanan';
                  if (selected.length === 1) {
                    if (selected[0] === 'unassigned') return 'Atanmamış';
                    const user = supportUsers.find(u => u.id.toString() === selected[0]);
                    return user ? user.name : '1 adet';
                  }
                  return `${selected.length} adet seçili`;
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      maxHeight: 300,
                      '& .MuiMenuItem-root': {
                        paddingLeft: '8px',
                        paddingRight: '8px',
                        justifyContent: 'flex-start'
                      }
                    }
                  }
                }}
                sx={{
                  '& .MuiSelect-select': { 
                    fontSize: '0.75rem',
                    padding: '6px 15px',
                    minHeight: '24px !important',
                    lineHeight: '1.2',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start'
                  }
                }}
              >
                <MenuItem 
                  value="unassigned" 
                  sx={{ 
                    fontSize: '0.75rem', 
                    py: 0.5,
                    display: 'flex',
                    justifyContent: 'flex-start',
                    paddingLeft: '8px',
                    fontWeight: filters.assigned_to.includes('unassigned') ? 'bold' : 'normal'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                    {filters.assigned_to.includes('unassigned') ? (
                      <CheckedIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                    ) : (
                      <UncheckedIcon sx={{ fontSize: 16, color: 'grey.400' }} />
                    )}
                    Atanmamış
                  </Box>
                </MenuItem>
                {supportUsers.map(user => {
                  const isSelected = filters.assigned_to.includes(user.id.toString());
                  return (
                    <MenuItem 
                      key={user.id} 
                      value={user.id.toString()} 
                      sx={{ 
                        fontSize: '0.75rem', 
                        py: 0.5,
                        display: 'flex',
                        justifyContent: 'flex-start',
                        paddingLeft: '8px',
                        fontWeight: isSelected ? 'bold' : 'normal'
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                        {isSelected ? (
                          <CheckedIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                        ) : (
                          <UncheckedIcon sx={{ fontSize: 16, color: 'grey.400' }} />
                        )}
                        {user.name}
                      </Box>
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Grid>
        )}

        {/* Destek ID Arama */}
        <Grid item xs={12} md={1.5} sx={{ mt: 1 }}>
          <OptimizedTextField
            fullWidth
            size="small"
            placeholder="Destek ID ara..."
            value={filters.id_search}
            onChange={(value: string) => handleFilterChange('id_search', value)}
            debounceTime={300}
            InputProps={{
              startAdornment: <SearchIcon fontSize="small" color="action" sx={{ fontSize: '16px' }} />,
              sx: { 
                '& input': {
                  padding: '6px 8px',
                  height: '24px !important',
                  fontSize: '0.75rem'
                }
              }
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                paddingLeft: '8px'
              }
            }}
          />
        </Grid>

        {/* Filtre Butonları */}
        <Grid item xs={12} md={2.8} sx={{ mt: 1 }}>
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'nowrap' }}>
            
            <Button
              variant="outlined"
              size="small"
              onClick={() => setDetailedFilterOpen(!detailedFilterOpen)}
              endIcon={
                <ExpandMoreIcon
                  sx={{
                    transform: detailedFilterOpen ? 'rotate(180deg)' : 'none',
                    transition: 'transform 0.2s ease',
                    fontSize: '18px',
                  }}
                />
              }
              sx={commonButtonSx}
            >
              Detaylı Filtre
            </Button>

            <Button
              variant="contained"
              onClick={onLoadTickets}
              sx={{
                ...commonButtonSx,
                fontWeight: 'bold',
                bgcolor: 'primary.main',
                color: 'white',
                '&:hover': {
                  bgcolor: 'primary.dark',
                },
              }}
            >
              Listele
            </Button>

            <Button
              variant="contained"
              onClick={onClearFilters}
              sx={{
                ...commonButtonSx,
                fontWeight: 'bold',
                bgcolor: '#d32f2f',
                color: 'white',
                '&:hover': {
                  bgcolor: '#b71c1c',
                },
              }}
            >
              Temizle
            </Button>
          </Box>
        </Grid>
        <Grid item xs="auto" sx={{ 
          mt: 0.2, // ✅ Hafif yukarı al
          ml: 'auto', // ✅ En sağa yapıştır
          pr: 1, // Sağdan boşluk
          display: 'flex',
          alignItems: 'center', // ✅ Dikeyde ortalama
          height: '100%' // ✅ Yüksekliği tam al
        }}>
          {(authUser?.role === 'admin' || authUser?.role === 'customer') && onNewTicket && (
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={onNewTicket}
          sx={{
            fontSize: '0.7rem',
            fontWeight: 'bold',
            height: '36px', // ✅ Daha iyi yükseklik
            minWidth: '120px', // ✅ Daha iyi genişlik
            backgroundColor: '#2e7d32',
            '&:hover': {
              backgroundColor: '#1b5e20',
            },
            whiteSpace: 'nowrap',
            padding: '8px 16px' // ✅ Daha iyi padding
          }}
        >
          YENİ TALEP
        </Button>
          )}
        </Grid>
      </Grid>

      {/* Detaylı Filtreler - Collapse */}
      <Collapse in={detailedFilterOpen}>
        <Box sx={{ mt: 1, p: 1,ml:-1, borderTop: '1px solid #e0e0e0' }}>
          <Grid container spacing={1} alignItems="flex-start">
            {/* Son Güncelleme Tarih Aralığı */}
            <Grid item xs={12} md={2.2}>
              <Box sx={{ display: 'flex', gap: 0.5, mt:1 }}>
                <TextField
                  fullWidth
                  type="date"
                  label="Son Güncelleme Başlangıç"
                  value={filters.dateRangeUpdated.start}
                  onChange={(e) => handleDateRangeChange('updated', 'start', e.target.value)}
                  InputLabelProps={{ 
                    shrink: true,
                    sx: { fontSize: '0.75rem', lineHeight: '1.2' }
                  }}
                  sx={{ 
                    '& .MuiInputBase-input': { 
                      fontSize: '0.75rem',
                      padding: '6px 8px',
                      height: '24px !important'
                    }
                  }}
                />
                <TextField
                  fullWidth
                  type="date"
                  label="Son Güncelleme Bitiş"
                  value={filters.dateRangeUpdated.end}
                  onChange={(e) => handleDateRangeChange('updated', 'end', e.target.value)}
                  InputLabelProps={{ 
                    shrink: true,
                    sx: { fontSize: '0.75rem', lineHeight: '1.2' }
                  }}
                  sx={{ 
                    '& .MuiInputBase-input': { 
                      fontSize: '0.75rem',
                      padding: '6px 8px',
                      height: '24px !important'
                    }
                  }}
                />
              </Box>
            </Grid>

            {/* Program Filtresi */}
            <Grid item xs={12} md={1.51} sx={{ mt: 1 }}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ fontSize: '0.75rem', lineHeight: '1.2' }}>Program</InputLabel>
                <Select 
                  value={filters.module} 
                  label="Program" 
                  onChange={(e) => handleFilterChange('module', typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                  multiple
                  renderValue={(selected) => {
                    if (selected.length === 0) return 'Program';
                    if (selected.length === 1) {
                      const module = modules.find(m => m.id.toString() === selected[0]);
                      return module ? module.name : '1 adet';
                    }
                    return `${selected.length} adet seçili`;
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        maxHeight: 300,
                        '& .MuiMenuItem-root': {
                          paddingLeft: '8px',
                          paddingRight: '8px',
                          justifyContent: 'flex-start'
                        }
                      }
                    }
                  }}
                  sx={{
                    '& .MuiSelect-select': { 
                      fontSize: '0.75rem',
                      padding: '6px 15px',
                      minHeight: '24px !important',
                      lineHeight: '1.2',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-start'
                    }
                  }}
                >
                  {modules.map(module => {
                    const isSelected = filters.module.includes(module.id.toString());
                    return (
                      <MenuItem 
                        key={module.id} 
                        value={module.id.toString()} 
                        sx={{ 
                          fontSize: '0.75rem', 
                          py: 0.5,
                          display: 'flex',
                          justifyContent: 'flex-start',
                          paddingLeft: '8px',
                          fontWeight: isSelected ? 'bold' : 'normal'
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                          {isSelected ? (
                            <CheckedIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                          ) : (
                            <UncheckedIcon sx={{ fontSize: 16, color: 'grey.400' }} />
                          )}
                          {module.name}
                        </Box>
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>

            {/* Öncelik Filtresi - Sadece Admin ve Support */}
            {(authUser?.role === 'admin' || authUser?.role === 'support') && (
              <Grid item xs={12} md={1.51} sx={{ mt: 1 }}>
                <FormControl fullWidth size="small">
                  <InputLabel sx={{ fontSize: '0.75rem', lineHeight: '1.2' }}>Öncelik</InputLabel>
                  <Select 
                    value={filters.priority} 
                    label="Öncelik" 
                    onChange={(e) => handleFilterChange('priority', typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                    multiple
                    renderValue={(selected) => {
                      if (selected.length === 0) return 'Öncelik';
                      if (selected.length === 1) {
                        const priority = priorities.find(p => p.id.toString() === selected[0]);
                        return priority ? priority.name : '1 adet';
                      }
                      return `${selected.length} adet seçili`;
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          maxHeight: 300,
                          '& .MuiMenuItem-root': {
                            paddingLeft: '8px',
                            paddingRight: '8px',
                            justifyContent: 'flex-start'
                          }
                        }
                      }
                    }}
                    sx={{
                      '& .MuiSelect-select': { 
                        fontSize: '0.75rem',
                        padding: '6px 15px',
                        minHeight: '24px !important',
                        lineHeight: '1.2',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-start'
                      }
                    }}
                  >
                    {priorities.map(priority => {
                      const isSelected = filters.priority.includes(priority.id.toString());
                      return (
                        <MenuItem 
                          key={priority.id} 
                          value={priority.id.toString()} 
                          sx={{ 
                            fontSize: '0.75rem', 
                            py: 0.5,
                            display: 'flex',
                            justifyContent: 'flex-start',
                            paddingLeft: '8px',
                            fontWeight: isSelected ? 'bold' : 'normal'
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                            {isSelected ? (
                              <CheckedIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                            ) : (
                              <UncheckedIcon sx={{ fontSize: 16, color: 'grey.400' }} />
                            )}
                            {getFilterPriorityChip(priority.id)}
                          </Box>
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Grid>
            )}

            {/* Firma Filtresi - Sadece Admin ve Support */}
            {(authUser?.role === 'admin' || authUser?.role === 'support') && (
              <Grid item xs={12} md={1.51} sx={{ mt: 1 }}>
                <FormControl fullWidth size="small">
                  <InputLabel sx={{ fontSize: '0.75rem', lineHeight: '1.2' }}>Firma</InputLabel>
                  <Select 
                    value={filters.company} 
                    label="Firma" 
                    onChange={(e) => handleFilterChange('company', typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                    multiple
                    renderValue={(selected) => {
                      if (selected.length === 0) return 'Firma';
                      if (selected.length === 1) {
                        const company = companies.find(c => c.id.toString() === selected[0]);
                        return company ? company.name : '1 adet';
                      }
                      return `${selected.length} adet seçili`;
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          maxHeight: 300,
                          '& .MuiMenuItem-root': {
                            paddingLeft: '8px',
                            paddingRight: '8px',
                            justifyContent: 'flex-start'
                          }
                        }
                      }
                    }}
                    sx={{
                      '& .MuiSelect-select': { 
                        fontSize: '0.75rem',
                        padding: '6px 15px',
                        minHeight: '24px !important',
                        lineHeight: '1.2',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-start'
                      }
                    }}
                  >
                    {companies.map(company => {
                      const isSelected = filters.company.includes(company.id.toString());
                      return (
                        <MenuItem 
                          key={company.id} 
                          value={company.id.toString()} 
                          sx={{ 
                            fontSize: '0.75rem', 
                            py: 0.5,
                            display: 'flex',
                            justifyContent: 'flex-start',
                            paddingLeft: '8px',
                            fontWeight: isSelected ? 'bold' : 'normal'
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                            {isSelected ? (
                              <CheckedIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                            ) : (
                              <UncheckedIcon sx={{ fontSize: 16, color: 'grey.400' }} />
                            )}
                            {company.name}
                          </Box>
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Grid>
            )}

            {/* Arama */}
            <Grid item xs={12} md={1.51} sx={{ mt: 1 }}>
              <OptimizedTextField
                fullWidth
                size="small"
                placeholder="Talep konusu ara..."
                value={filters.search}
                onChange={(value: string) => handleFilterChange('search', value)}
                debounceTime={300}
                InputProps={{
                  startAdornment: <SearchIcon fontSize="small" color="action" sx={{ fontSize: '16px' }} />,
                  sx: { 
                    '& input': {
                      padding: '6px 8px',
                      height: '24px !important',
                      fontSize: '0.75rem'
                    }
                  }
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    paddingLeft: '8px'
                  }
                }}
              />
            </Grid>
          </Grid>
        </Box>
      </Collapse>

      {/* Aktif Filtreler Göstergesi */}
      <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.3 }}>
        {/* Durum Filtreleri */}
        {filters.status.map((id: string) => (
          <Chip 
            key={`status-${id}`}
            label={`Durum: ${statuses.find(s => s.id.toString() === id)?.name}`}
            onDelete={() => handleFilterChange('status', filters.status.filter((v: string) => v !== id))}
            size="small"
            color="primary"
            variant="outlined"
            sx={{ fontSize: '0.65rem', height: '22px' }}
          />
        ))}
        
        {/* Program Filtreleri */}
        {filters.module.map((id: string) => (
          <Chip 
            key={`module-${id}`}
            label={`Program: ${modules.find(m => m.id.toString() === id)?.name}`}
            onDelete={() => handleFilterChange('module', filters.module.filter((v: string) => v !== id))}
            size="small"
            color="secondary"
            variant="outlined"
            sx={{ fontSize: '0.65rem', height: '22px' }}
          />
        ))}
        
        {/* Öncelik Filtreleri */}
        {filters.priority.map((id: string) => (
          <Chip 
            key={`priority-${id}`}
            label={`Öncelik: ${priorities.find(p => p.id.toString() === id)?.name}`}
            onDelete={() => handleFilterChange('priority', filters.priority.filter((v: string) => v !== id))}
            size="small"
            color="warning"
            variant="outlined"
            sx={{ fontSize: '0.65rem', height: '22px' }}
          />
        ))}
        
        {/* Atanan Filtreleri */}
        {filters.assigned_to.map((id: string) => (
          <Chip 
            key={`assigned-${id}`}
            label={`Atanan: ${id === 'unassigned' ? 'Atanmamış' : supportUsers.find(u => u.id.toString() === id)?.name}`}
            onDelete={() => handleFilterChange('assigned_to', filters.assigned_to.filter((v: string) => v !== id))}
            size="small"
            color="info"
            variant="outlined"
            sx={{ fontSize: '0.65rem', height: '22px' }}
          />
        ))}
        
        {/* Firma Filtreleri */}
        {filters.company.map((id: string) => (
          <Chip 
            key={`company-${id}`}
            label={`Firma: ${companies.find(c => c.id.toString() === id)?.name}`}
            onDelete={() => handleFilterChange('company', filters.company.filter((v: string) => v !== id))}
            size="small"
            color="default"
            variant="outlined"
            sx={{ fontSize: '0.65rem', height: '22px' }}
          />
        ))}
        
        {/* Arama Filtresi */}
        {filters.search && (
          <Chip 
            label={`Arama: ${filters.search}`}
            onDelete={() => handleFilterChange('search', '')}
            size="small"
            color="success"
            variant="outlined"
            sx={{ fontSize: '0.65rem', height: '22px' }}
          />
        )}
        
        {/* Destek ID Arama Filtresi */}
        {filters.id_search && (
          <Chip 
            label={`Destek ID: ${filters.id_search}`}
            onDelete={() => handleFilterChange('id_search', '')}
            size="small"
            color="info"
            variant="outlined"
            sx={{ fontSize: '0.65rem', height: '22px' }}
          />
        )}
        
        {/* Oluşturma Tarih Filtresi */}
        {(filters.dateRangeCreated.start !== defaultStart || filters.dateRangeCreated.end !== defaultEnd) && (
          <Chip 
            label={`Oluşturma: ${filters.dateRangeCreated.start} - ${filters.dateRangeCreated.end}`}
            onDelete={() => handleFilterChange('dateRangeCreated', { start: defaultStart, end: defaultEnd })}
            size="small"
            color="secondary"
            variant="outlined"
            sx={{ fontSize: '0.65rem', height: '22px' }}
          />
        )}
        
        {/* Güncelleme Tarih Filtresi */}
        {(filters.dateRangeUpdated.start !== defaultStart || filters.dateRangeUpdated.end !== defaultEnd) && (
          <Chip 
            label={`Güncelleme: ${filters.dateRangeUpdated.start} - ${filters.dateRangeUpdated.end}`}
            onDelete={() => handleFilterChange('dateRangeUpdated', { start: defaultStart, end: defaultEnd })}
            size="small"
            color="warning"
            variant="outlined"
            sx={{ fontSize: '0.65rem', height: '22px' }}
          />
        )}
      </Box>
    </Paper>
  );
};